import React, { FC } from 'react';
import { BadgeVariants, Icon, TextBadge } from '@packages/ui/shared';
import { IconProps } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { StyleProp, ViewStyle, StyleSheet } from 'react-native';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

interface BmisBatteryBadgeProps {
    label: string | null | undefined;
    style?: StyleProp<ViewStyle>;
    isCampaign?: boolean;
}

const useStyles = createUseStyles({
    badgeWrapper: {
        overflow: 'hidden',
        minWidth: 0,
        maxWidth: '100%',
    },
    customTooltip: {
        fontSize: 12,
        padding: '8px 12px',
        textAlign: 'center',
        marginTop: '-4px !important',
    },
});

export const BmisBatteryBadge: FC<BmisBatteryBadgeProps> = ({ label, style, isCampaign = false }) => {
    const classes = useStyles();
    let variant: BadgeVariants;
    let iconProps: IconProps | undefined = undefined;

    if (!label) return null;
    const lowerCaseLabel = label.toLowerCase();

    if (isCampaign) {
        switch (lowerCaseLabel) {
            case 'open':
                variant = 'blue';
                break;
            case 'open parts limited':
                variant = 'redFive';
                break;
            case 'open no remedy yet':
                variant = 'redFive';
                break;
            case 'completed':
                variant = 'greenFive';
                break;
            case 'expired':
                variant = 'default';
                break;
            default:
                variant = 'gray';
                break;
        }
    } else {
        switch (lowerCaseLabel) {
            case 'good battery':
            case 'good recharge':
            case 'in balance':
            case 'pdi complete':
                variant = 'green';
                iconProps = { name: 'check-circle-hollow' as const, color: 'greenOne' };
                break;

            case '24 volt battery':
            case 'aborted':
            case 'aborted/24v':
            case 'charge & retest':
            case 'clamps removed':
            case 'gr8 diagnostic needed':
            case 'lost power':
            case 'marginal battery':
            case 'marginal recharge':
            case 'out of balance':
            case 'temp sensor failed':
                variant = 'yellow';
                iconProps = { name: 'warning' as const, color: 'yellowOne' };
                break;

            case 'bad cell replace':
            case 'bad cell short replace':
            case 'bc open or load fail replace':
            case 'broken weld replace':
            case 'frozen battery':
            case 'replace battery':
            case 'replace load fail':
            case 'too hot replace':
                variant = 'red';
                iconProps = { name: 'x-circle' as const, color: 'redOne' };
                break;

            case 'invalid test':
            case 'remote post':
            case 'side post':
                variant = 'gray';
                iconProps = { name: 'circle-indeterminate' as const, color: 'black' };
                break;

            case lowerCaseLabel.endsWith('ago') ? lowerCaseLabel : undefined:
                variant = 'gray';
                iconProps = { name: 'schedule', color: 'grayFive' };
                break;

            default:
                variant = 'gray';
                iconProps = { name: 'circle-indeterminate' as const, color: 'black' };
                break;
        }
    }

    const flattenedStyle = StyleSheet.flatten([{ paddingLeft: 8, paddingRight: 8 }, style]);

    return (
        <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={
                <Tooltip id="battery-badge-tooltip" className={classes.customTooltip}>
                    {label ? label : 'N/A'}
                </Tooltip>
            }
        >
            <div className={classes.badgeWrapper}>
                <TextBadge
                    style={flattenedStyle}
                    variant={variant}
                    icon={!isCampaign && iconProps ? <Icon {...iconProps} size={16} /> : undefined}
                    typographyProps={{ numberOfLines: 1 }}
                >
                    {label ? label : 'N/A'}
                </TextBadge>
            </div>
        </OverlayTrigger>
    );
};
